import dayjs from "dayjs";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraEmpty } from "../../components/AtiraEmpty";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { SubHeader } from "../../components/SubHeader";
import { Text } from "../../components/Text";
import { orgChatSliceSelectors } from "../../redux/chat/orgChat.selector";
import { orgChatActions } from "../../redux/chat/orgChat.slice";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { TestIDs } from "../../tests/types/testIds";
import { Rounded } from "../../theme/Rounded";
import { Spacing } from "../../theme/Spacing";
import { PageIcons } from "../../utils/GlobalIcons";
import { OrgChatComposer } from "./components/OrgChatComposer";
import { OrgChatMembers } from "./components/OrgChatMembers";
import { AtiraImage } from "../../components/Image";
import { WAUserAvatar } from "../integration/components/whatsapp-chat/components/WAUserAvatar";

const Container = styled(Flex)`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  margin: 0 ${Spacing.l} ${Spacing.m};
  background: ${({ theme }) => theme.white};
  border: 1px solid ${({ theme }) => theme.darkSub};
  border-radius: ${Rounded.md};
  box-shadow: 0 10px 34px rgba(0, 0, 0, 0.08);
`;

const MessageList = styled(Flex)`
  flex: 1;
  overflow-y: auto;
  padding: ${Spacing.m};
  background: ${({ theme }) => theme.lightSub};
  direction: ltr;
`;

const Bubble = styled(Flex)<{ $own: boolean }>`
  align-self: ${({ $own }) => ($own ? "flex-end" : "flex-start")};
  background-color: ${({ theme, $own }) =>
    $own ? `${theme.main}22` : theme.white};
  border: 1px solid
    ${({ theme, $own }) => ($own ? `${theme.main}44` : theme.sub)};
  border-radius: ${Rounded.md};
  padding: ${Spacing.s} ${Spacing.m};
  max-width: 60%;
  word-break: break-word;
`;

export const OrgChatRoute: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const listRef = useRef<HTMLDivElement>(null);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId);
  const messages = useAppSelector(orgChatSliceSelectors.selectMessages);
  const hasMore = useAppSelector(orgChatSliceSelectors.selectHasMore);
  const loading = useAppSelector(orgChatSliceSelectors.selectLoading);

  useEffect(() => {
    dispatch(orgChatActions.getMessages({}));
    dispatch(orgChatActions.getMembers());
    dispatch(orgChatActions.markAsRead());
  }, [dispatch]);

  // New message (own or incoming) - keep the newest bubble in view. Loading
  // an older page on top intentionally does not trigger this: it would yank
  // the view away from whatever the member scrolled up to read.
  useEffect(() => {
    if (!listRef.current) {
      return;
    }

    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  const handleLoadMore = () => {
    dispatch(orgChatActions.getMessages({ beforeId: messages[0]?._id }));
  };

  return (
    <Flex flexDirection="column" flex={1} height="100%">
      <SubHeader title={t("teamChat.title")} icon={PageIcons.TeamChat} />

      <Container>
        <OrgChatMembers />

        <Flex flexDirection="column" flex={1} minWidth="0">
          <MessageList
            ref={listRef}
            flexDirection="column"
            gap="s"
            dataTestId={TestIDs.orgChat.messageList}
          >
            {hasMore ? (
              <Flex justifyContent="center">
                <Button
                  backgroundColor="white"
                  color="main"
                  loading={loading}
                  onClick={handleLoadMore}
                  dataTestId={TestIDs.orgChat.loadMore}
                >
                  {t("teamChat.loadMore")}
                </Button>
              </Flex>
            ) : null}

            {!loading && !messages.length ? (
              <AtiraEmpty description={t("teamChat.empty")} />
            ) : null}

            {messages.map((message) => {
              const own = message.senderId === userId;

              return (
                <Bubble
                  key={message._id}
                  flexDirection="column"
                  $own={own}
                  dataTestId={TestIDs.orgChat.message(message._id)}
                >
                  <Flex
                    gap="xs"
                    justifyContent={!own ? "flex-start" : "flex-end"}
                  >
                    <Text
                      fontSize="xs"
                      color="main"
                      fontWeight="bold"
                      dir="auto"
                    >
                      {message.sender?.name}
                    </Text>

                    <WAUserAvatar user={message.sender} size="1rem" />
                  </Flex>
                  <Text fontSize="m" dir="auto">
                    {message.text}
                  </Text>
                  <Text fontSize="xs" color="darkerSub" align="end">
                    {dayjs(message.createdAt).format("HH:mm")}
                  </Text>
                </Bubble>
              );
            })}
          </MessageList>

          <OrgChatComposer />
        </Flex>
      </Container>
    </Flex>
  );
};
